import { isNil } from 'lodash';
import { Text } from '@shared/components/Text';
import { Colors } from '@shared/styles';
import { API } from '@shared/types/api';
import { formatChannelNameForDisplay } from '@shared/util/emoji';
import { useCurrentBusiness, useDmChannelUsers } from '@shared/util/hooks';
import { i18n } from '@shared/util/i18n';
import { MBBaseChatBanner } from './base';
import { MBChannelChatBanner } from './channel';
import { MBDMChatBanner } from './dm';
import { MBMeChatBanner } from './me';
import { MBMultiDMChatBanner } from './multiDm';

interface MBChatBannerProps {
  channel: API.ChannelFragment;
  creator: API.ShortUserFragment;
}

/*
    Chat channel banner at the top of every chat thread.
*/
export function MBChatBannerComponent({ channel, creator }: MBChatBannerProps) {
  // Clicking Support opens the intercom UI, not a workspace
  if (channel.type === 'SUPPORT') {
    return null;
  }

  if (channel.type === 'DM') {
    return <DMChannelBanner channel={channel} />;
  }

  return <MBChannelChatBanner channel={channel} creator={creator} />;
}

function DMChannelBanner({ channel }: Omit<MBChatBannerProps, 'creator'>) {
  const business = useCurrentBusiness();
  const { users, membershipCount, loading } = useDmChannelUsers(channel.id);

  if (loading || isNil(business)) {
    return null;
  }

  const otherUsers = users.filter(u => !u.isMe);
  const meUser = users.find(u => u.isMe);

  if (otherUsers.length === 1) {
    return <MBDMChatBanner channel={channel} user={otherUsers[0]} />;
  }
  if (otherUsers.length > 1) {
    return <MBMultiDMChatBanner channel={channel} users={otherUsers} />;
  }

  const isPersonalWorkspace = (membershipCount ?? users.length) <= 1;
  if (isPersonalWorkspace && meUser) {
    return <MBMeChatBanner channel={channel} user={meUser} />;
  }

  const displayName = formatChannelNameForDisplay(channel);
  const deactivatedName = displayName || i18n('directory.deactivated');
  return (
    <MBBaseChatBanner title={deactivatedName} channel={channel}>
      <Text color={Colors.v2.greenSecondary90}>
        {i18n('directory.actions.deactivatedTitle', {
          name: deactivatedName,
          business: business.name,
        })}
      </Text>
    </MBBaseChatBanner>
  );
}
